import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { lazy, Suspense } from "react";
import axios from "axios";

import "./i18n.js"; // Initialize i18next
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClientProvider } from '@tanstack/react-query';
import ErrorBoundary from "./components/ErrorBoundary.js";
import RuntimeIdentityGate from "./components/RuntimeIdentityGate.js";
import Layout from "./components/Layout.js";
import { ThemeProvider } from './contexts/ThemeContext.js';
import { AssistantProvider } from './components/assistant/AssistantProvider.js';
import { queryClient } from './queryClient.js';
import { getFirstAllowedRoute, hasRoutePermission, normalizePermissions, resolveRoutePermission } from './utils/permissions.js';
import { resolveBackendBase } from './utils/backendBase.js';
import { installAuthInterceptors } from './utils/authToken.js';
import { Toaster } from './components/ui/toaster.js';
// Eager load critical pages
import Dashboard from "./pages/Dashboard.js";
import Login from "./pages/Login.js";
import PartsInventory from "./pages/PartsInventory.js";
// Lazy load other pages for better performance
import Customers from "./pages/Customers.js";
import NewVehicle from "./pages/NewVehicle.js";
import VehicleDetails from "./pages/VehicleDetails.js";
import CustomerDetails from "./pages/CustomerDetails.js";
import Technicians from "./pages/Technicians.js";
import Suppliers from "./pages/Suppliers.js";
import PartsDashboard from "./pages/PartsDashboard.js";
import ServicesManagement from "./pages/ServicesManagement.js";
import InvoiceDesignerStudio from "./pages/InvoiceDesignerStudio.js";
import Settings from "./pages/Settings.js";
import VehicleArchive from "./pages/VehicleArchive.js";
import Operations from "./pages/Operations.js";
import ApprovalPublic from "./pages/ApprovalPublic.js";
import AuthCallback from "./pages/AuthCallback.js";
import AccountSecurity from "./pages/AccountSecurity.js";
import AccountSetup from "./pages/AccountSetup.js";
import ReportPublic from "./pages/ReportPublic.js";
import CustomerTracking from "./pages/CustomerTracking.js";
import QuotationGenerator from "./pages/QuotationGenerator.js";
import DocumentPrint from "./pages/DocumentPrint.js";
import TemplatesManager from "./pages/TemplatesManager.js";
import Taxes from "./pages/Taxes.js";
import Invoices from "./pages/Invoices.js";
import ChartOfAccounts from "./pages/ChartOfAccountsLiquid.js";
import JournalEntries from "./pages/JournalEntries.js";
import FinancialStatements from "./pages/FinancialStatements.js";
import FirewallPanel from "./pages/FirewallPanel.js";
import SystemAudit from "./pages/SystemAudit.js";
import DebtFollowUp from "./pages/DebtFollowUp.js";

// Keep the existing guarded route while replacing the frozen legacy screen
// with the dedicated read-only statement surface.
const ComprehensiveFinancial = FinancialStatements;

const WorkshopProfile = lazy(() => import("./pages/WorkshopProfile.js"));
const ImportPage = lazy(() => import("./pages/Import.js"));
const Users = lazy(() => import("./pages/UsersManagement.js"));
const AIFinancial = lazy(() => import("./pages/AIFinancial.js"));

// 🔒 Install JWT interceptors globally — keeps access JWTs only in memory and
// uses the httpOnly refresh cookie after a reload.
installAuthInterceptors(axios);
// Loading component
const PageLoader = () => (_jsx("div", { className: "flex items-center justify-center min-h-screen", children: _jsx("div", { className: "w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" }) }));
const Protected = ({ children }) => {
    const location = useLocation();
    const readSession = () => {
        try {
            return JSON.parse(localStorage.getItem('session') || 'null');
        }
        catch (e) {
            return null;
        }
    };
    const [session, setSession] = React.useState(() => readSession());
    React.useEffect(() => {
        const sync = () => setSession(readSession());
        // تحديث عند تغيّر localStorage من نافذة أخرى
        window.addEventListener('storage', sync);
        // تحديث داخل نفس الصفحة (login/logout/expiry)
        window.addEventListener('sessionUpdated', sync);
        window.addEventListener('auth:session-expired', sync);
        return () => {
            window.removeEventListener('storage', sync);
            window.removeEventListener('sessionUpdated', sync);
            window.removeEventListener('auth:session-expired', sync);
        };
    }, []);
    React.useEffect(() => {
        if (!session?.id)
            return undefined;
        let cancelled = false;
        const refreshSessionPermissions = async () => {
            try {
                // Self-only endpoint; never fetch the organization user list to
                // reconstruct a browser session.
                const res = await fetch(`${resolveBackendBase()}/api/auth/me`, { cache: 'no-store' });
                if (cancelled)
                    return;
                if (!res.ok) {
                    if (res.status === 401) {
                        localStorage.removeItem('session');
                        localStorage.removeItem('user');
                        setSession(null);
                    }
                    return;
                }
                const identity = await res.json();
                const sameIdentity = String(identity?.user_id || '') === String(session.id || '');
                const nextSession = {
                    ...session,
                    id: identity?.user_id || session.id,
                    username: identity?.username || session.username,
                    name: sameIdentity ? session.name : (identity?.username || session.username),
                    phone: sameIdentity ? session.phone || '' : '',
                    email: sameIdentity ? session.email || '' : '',
                    role: identity?.role || session.role,
                    permissions: normalizePermissions(identity?.permissions, identity?.role || session.role),
                };
                const currentSerialized = JSON.stringify(session.permissions || {});
                const nextSerialized = JSON.stringify(nextSession.permissions || {});
                if (currentSerialized !== nextSerialized || nextSession.role !== session.role ||
                    nextSession.name !== session.name || nextSession.username !== session.username) {
                    localStorage.setItem('session', JSON.stringify(nextSession));
                    localStorage.setItem('user', JSON.stringify({
                        ...nextSession,
                        isActive: true,
                    }));
                    window.dispatchEvent(new Event('sessionUpdated'));
                    setSession(nextSession);
                }
            }
            catch (e) {
                // keep current session if refresh fails
            }
        };
        refreshSessionPermissions();
        return () => {
            cancelled = true;
        };
    }, [session?.id, session?.username]);
    if (!session?.id || !session?.username) {
        return _jsx(Login, {});
    }
    const currentRoute = `${location.pathname}${location.search || ''}`;
    const routePermission = resolveRoutePermission(currentRoute);
    if (!routePermission && location.pathname !== '/') {
        const fallbackPath = getFirstAllowedRoute(session);
        return _jsx(Navigate, { to: fallbackPath, replace: true });
    }
    if (routePermission && !hasRoutePermission(session, routePermission)) {
        const fallbackPath = getFirstAllowedRoute(session);
        return _jsx(Navigate, { to: fallbackPath === currentRoute ? '/login' : fallbackPath, replace: true });
    }
    return children;
};
function App() {
    // Google SSO: process session_id from the URL fragment BEFORE any routing/auth checks.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (typeof window !== 'undefined' && (window.location.hash || '').includes('session_id=')) {
        return (_jsx(ErrorBoundary, { children: _jsx(Suspense, { fallback: _jsx(PageLoader, {}), children: _jsx(AuthCallback, {}) }) }));
    }
    return (_jsx(ErrorBoundary, { children: _jsx(RuntimeIdentityGate, { children: _jsx(QueryClientProvider, { client: queryClient, children: _jsx(ThemeProvider, { children: _jsx(AssistantProvider, { children: _jsxs("div", { className: "App wms-theme", style: { backgroundColor: 'var(--bg-primary)', minHeight: '100vh' }, children: [_jsx(Router, { children: _jsx(Suspense, { fallback: _jsx(PageLoader, {}), children: _jsxs(Routes, { children: [_jsx(Route, { path: "/login", element: _jsx(Login, {}) }), _jsx(Route, { path: "/account/setup", element: _jsx(AccountSetup, {}) }), _jsx(Route, { path: "/approval/:token", element: _jsx(ApprovalPublic, {}) }), _jsx(Route, { path: "/report/:token", element: _jsx(ReportPublic, {}) }), _jsx(Route, { path: "/track/:trackingId", element: _jsx(CustomerTracking, {}) }), _jsxs(Route, { path: "/", element: _jsx(Protected, { children: _jsx(Layout, {}) }), children: [_jsx(Route, { index: true, element: _jsx(Dashboard, {}) }), _jsx(Route, { path: "customers", element: _jsx(Customers, {}) }), _jsx(Route, { path: "new-vehicle", element: _jsx(NewVehicle, {}) }), _jsx(Route, { path: "vehicle/:id", element: _jsx(VehicleDetails, {}) }), _jsx(Route, { path: "customer/:id", element: _jsx(CustomerDetails, {}) }), _jsx(Route, { path: "technicians", element: _jsx(Technicians, {}) }), _jsx(Route, { path: "suppliers", element: _jsx(Suppliers, {}) }), _jsx(Route, { path: "debts-followup", element: _jsx(DebtFollowUp, {}) }), _jsx(Route, { path: "parts", element: _jsx(PartsInventory, {}) }), _jsx(Route, { path: "parts-dashboard", element: _jsx(PartsDashboard, {}) }), _jsx(Route, { path: "catalog", element: _jsx(Navigate, { to: "/parts", replace: true }) }), _jsx(Route, { path: "services", element: _jsx(ServicesManagement, {}) }), _jsx(Route, { path: "invoice-templates", element: _jsx(InvoiceDesignerStudio, {}) }), _jsx(Route, { path: "settings", element: _jsx(Settings, {}) }), _jsx(Route, { path: "account/security", element: _jsx(AccountSecurity, {}) }), _jsx(Route, { path: "profile", element: _jsx(Navigate, { to: "/settings?tab=profile", replace: true }) }), _jsx(Route, { path: "archive", element: _jsx(VehicleArchive, {}) }), _jsx(Route, { path: "operations", element: _jsx(Operations, {}) }), _jsx(Route, { path: "import", element: _jsx(Navigate, { to: "/settings?tab=import", replace: true }) }), _jsx(Route, { path: "users", element: _jsx(Navigate, { to: "/settings?tab=users", replace: true }) }), _jsx(Route, { path: "quotations", element: _jsx(QuotationGenerator, {}) }), _jsx(Route, { path: "print", element: _jsx(DocumentPrint, {}) }), _jsx(Route, { path: "templates", element: _jsx(TemplatesManager, {}) }), _jsx(Route, { path: "denso-diagnostics", element: _jsx(Navigate, { to: "/", replace: true }) }), _jsx(Route, { path: "fault-knowledge", element: _jsx(Navigate, { to: "/", replace: true }) }), _jsx(Route, { path: "accounting/test", element: _jsx("div", { "data-testid": "accounting-test", children: "TEST ACCOUNTING" }) }), _jsx(Route, { path: "finance/invoices", element: _jsx(Invoices, {}) }), _jsx(Route, { path: "finance/taxes", element: _jsx(Taxes, {}) }), _jsx(Route, { path: "accounting/chart-of-accounts", element: _jsx(ChartOfAccounts, {}) }), _jsx(Route, { path: "accounting/comprehensive", element: _jsx(ComprehensiveFinancial, {}) }), _jsx(Route, { path: "accounting/journal-entries", element: _jsx(JournalEntries, {}) }), _jsx(Route, { path: "accounting/firewall", element: _jsx(FirewallPanel, {}) }), _jsx(Route, { path: "ai-financial", element: _jsx(Navigate, { to: "/accounting/firewall", replace: true }) }), _jsx(Route, { path: "system-audit", element: _jsx(SystemAudit, {}) }), _jsx(Route, { path: "moltbot", element: _jsx(Navigate, { to: "/", replace: true }) }), _jsx(Route, { path: "*", element: _jsx(Dashboard, {}) })] })] }) }) }), _jsx(Toaster, {})] }) }) }) }) }) }));
}
export default App;
